/** Footer Editor — единая страница для всех настроек футера (Saleor-style).
 *
 * Объединяет в одном flow всё что было разбросано по
 * /site-content (тексты footer) и /configuration/navigation
 * (footer_column/footer_social/footer_apps/footer_legal).
 *
 * Архитектура:
 * - Newsletter тексты + QR + captions + legal_intro — единый draft state,
 *   общий sticky Save bar (PATCH /admin/v1/site-content).
 * - Колонки/Соцсети/Apps/Legal ссылки — навигация (POST/PATCH/DELETE
 *   per item, без общего drafт'а).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Plus,
  Rows3 as LayoutIcon,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ImageUploader } from "@/components/common/ImageUploader";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

// ─── Types ──────────────────────────────────────────────────────────────

const LANGS = ["ru", "uz", "en"] as const;
type Lang = (typeof LANGS)[number];
type I18n = Partial<Record<Lang, string>>;

interface SiteContentRaw {
  footer?: {
    address_line?: I18n;
    rights_template?: I18n;
    newsletter_heading?: I18n;
    newsletter_consent?: I18n;
    apps_qr_url?: I18n;
    apps_caption_top?: I18n;
    apps_caption_bottom?: I18n;
    legal_intro?: I18n;
  };
  [k: string]: unknown;
}

interface NavLink {
  id: number;
  location: string;
  parent_id: number | null;
  title: string;
  url: string;
  icon: string | null;
  badge_text: string | null;
  badge_color: string | null;
  open_in_new_tab: boolean;
  sort_order: number;
  is_visible: boolean;
}

// ─── Main page ──────────────────────────────────────────────────────────

export function FooterEditorPage(): JSX.Element {
  const qc = useQueryClient();

  const siteQ = useQuery({
    queryKey: ["admin-site-content"],
    queryFn: async () => {
      const { data } = await api.get<SiteContentRaw>("/site-content");
      return data;
    },
  });

  const navQ = useQuery({
    queryKey: ["nav-links"],
    queryFn: async () => {
      const { data } = await api.get<NavLink[]>("/navigation");
      return data;
    },
  });

  const [draft, setDraft] = useState<SiteContentRaw | null>(null);
  useEffect(() => {
    if (siteQ.data) setDraft(structuredClone(siteQ.data));
  }, [siteQ.data]);

  const dirty =
    !!draft && !!siteQ.data && JSON.stringify(draft) !== JSON.stringify(siteQ.data);

  const saveSiteContent = useMutation({
    mutationFn: async () => {
      if (!draft) return;
      await api.put("/site-content", draft);
    },
    onSuccess: () => {
      toast.success("Тексты футера сохранены");
      qc.invalidateQueries({ queryKey: ["admin-site-content"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  if (siteQ.isLoading || navQ.isLoading || !draft) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const patch = (mutator: (d: SiteContentRaw) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      mutator(next);
      return next;
    });
  };

  const footer = draft.footer ?? {};
  const navLinks = navQ.data ?? [];
  const columnLinks = navLinks.filter((l) => l.location === "footer_column");
  const socialLinks = navLinks.filter((l) => l.location === "footer_social");
  const appsLinks = navLinks.filter((l) => l.location === "footer_apps");
  const legalLinks = navLinks.filter((l) => l.location === "footer_legal");

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div>
        <h1 className="text-2xl font-heading flex items-center gap-2">
          <LayoutIcon className="w-6 h-6" />
          Футер сайта
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Все настройки футера в одном месте. Тексты сохраняются по кнопке
          внизу, ссылки/иконки — сразу при изменении.
        </p>
      </div>

      {/* 1. Newsletter */}
      <Section
        title="1. Newsletter (плашка подписки сверху футера)"
        description="Светлая полоса с email-формой над колонками."
      >
        <I18nField
          label="Заголовок"
          value={footer.newsletter_heading ?? {}}
          onChange={(v) =>
            patch((d) => {
              d.footer = { ...d.footer, newsletter_heading: v };
            })
          }
        />
        <I18nField
          label="Подзаголовок (текст согласия)"
          value={footer.newsletter_consent ?? {}}
          onChange={(v) =>
            patch((d) => {
              d.footer = { ...d.footer, newsletter_consent: v };
            })
          }
        />
      </Section>

      {/* 2. Колонки ссылок (tree) */}
      <Section
        title="2. Колонки ссылок"
        description="Дерево: колонка → ссылки или подзаголовок → ссылки. Изменения применяются сразу."
      >
        <ColumnsTree links={columnLinks} />
      </Section>

      {/* 3. QR блок */}
      <Section
        title="3. QR-карта мобильного приложения"
        description="Большой QR + подписи + кнопки сторов (Google Play, App Store)."
      >
        <div className="grid gap-4 md:grid-cols-[260px_1fr]">
          <div>
            <Label className="text-xs mb-1.5 block">Картинка QR-кода</Label>
            <ImageUploader
              value={footer.apps_qr_url?.ru ?? ""}
              hint="Загрузить QR (PNG/SVG)"
              previewClassName="h-40"
              onChange={(url) =>
                patch((d) => {
                  d.footer = {
                    ...d.footer,
                    apps_qr_url: { ru: url, uz: url, en: url },
                  };
                })
              }
              onRemove={() =>
                patch((d) => {
                  d.footer = {
                    ...d.footer,
                    apps_qr_url: { ru: "", uz: "", en: "" },
                  };
                })
              }
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Если пусто — блок QR в футере скрывается.
            </p>
          </div>
          <div className="space-y-3">
            <I18nField
              label="Подпись сверху"
              value={footer.apps_caption_top ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, apps_caption_top: v };
                })
              }
            />
            <I18nField
              label="Подпись снизу"
              value={footer.apps_caption_bottom ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, apps_caption_bottom: v };
                })
              }
            />
          </div>
        </div>

        <div className="mt-5 pt-5 border-t">
          <Label className="text-xs mb-2 block font-semibold">
            Кнопки сторов (под QR)
          </Label>
          <FlatLinksList
            links={appsLinks}
            location="footer_apps"
            placeholderText="Google Play"
            placeholderUrl="https://play.google.com/…"
            placeholderIcon="google"
            showIcon
            iconHint="apple / google / huawei / rustore"
          />
        </div>
      </Section>

      {/* 4. Соцсети */}
      <Section
        title="4. Соцсети"
        description="Иконки соцсетей в блоке «Наши соцсети» внизу футера."
      >
        <FlatLinksList
          links={socialLinks}
          location="footer_social"
          placeholderText="Telegram"
          placeholderUrl="https://t.me/…"
          placeholderIcon="telegram"
          showIcon
          iconHint="vk / telegram / instagram / youtube / whatsapp / rutube / ok / dzen / facebook / twitter"
        />
      </Section>

      {/* 5. Legal */}
      <Section
        title="5. Legal (текст и ссылки внизу)"
        description="Слова «политики обработки персональных данных» и «пользовательского соглашения» в тексте автоматически становятся ссылками на первые 2 элемента из списка."
      >
        <I18nField
          label="Вступительный текст"
          multiline
          value={footer.legal_intro ?? {}}
          onChange={(v) =>
            patch((d) => {
              d.footer = { ...d.footer, legal_intro: v };
            })
          }
        />
        <div className="mt-4 pt-4 border-t">
          <Label className="text-xs mb-2 block font-semibold">
            Legal-ссылки
          </Label>
          <FlatLinksList
            links={legalLinks}
            location="footer_legal"
            placeholderText="Политика обработки"
            placeholderUrl="/policy"
          />
        </div>
      </Section>

      {/* Sticky save bar for texts */}
      {dirty && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="container mx-auto flex items-center justify-between gap-4 p-3">
            <p className="text-sm text-muted-foreground">
              Тексты футера изменены, но не сохранены.
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setDraft(structuredClone(siteQ.data!))}
                disabled={saveSiteContent.isPending}
              >
                Откатить
              </Button>
              <Button
                onClick={() => saveSiteContent.mutate()}
                disabled={saveSiteContent.isPending}
              >
                {saveSiteContent.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section wrapper ───────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div>
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {description}
            </p>
          )}
        </div>
        <div className="space-y-3">{children}</div>
      </CardContent>
    </Card>
  );
}

// ─── I18n field (3 lang inputs side-by-side) ───────────────────────────

function I18nField({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: I18n;
  onChange: (v: I18n) => void;
  multiline?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="grid gap-2 md:grid-cols-3">
        {LANGS.map((lang) => (
          <div key={lang} className="space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {lang}
            </span>
            {multiline ? (
              <Textarea
                rows={3}
                value={value[lang] ?? ""}
                onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
              />
            ) : (
              <Input
                value={value[lang] ?? ""}
                onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Flat links list (для social/apps/legal) ───────────────────────────

function FlatLinksList({
  links,
  location,
  placeholderText,
  placeholderUrl,
  placeholderIcon,
  showIcon = false,
  iconHint,
}: {
  links: NavLink[];
  location: string;
  placeholderText: string;
  placeholderUrl: string;
  placeholderIcon?: string;
  showIcon?: boolean;
  iconHint?: string;
}) {
  const sorted = [...links].sort((a, b) => a.sort_order - b.sort_order);
  return (
    <div className="space-y-2">
      {sorted.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Пока пусто.</p>
      )}
      {sorted.map((link) => (
        <InlineLinkRow
          key={link.id}
          link={link}
          location={location}
          showIcon={showIcon}
          iconHint={iconHint}
        />
      ))}
      <CreateInlineLink
        location={location}
        showIcon={showIcon}
        placeholderText={placeholderText}
        placeholderUrl={placeholderUrl}
        placeholderIcon={placeholderIcon}
        iconHint={iconHint}
      />
    </div>
  );
}

function InlineLinkRow({
  link,
  location,
  showIcon,
  iconHint,
}: {
  link: NavLink;
  location: string;
  showIcon: boolean;
  iconHint?: string;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(link.title);
  const [url, setUrl] = useState(link.url);
  const [icon, setIcon] = useState(link.icon ?? "");
  const [sortOrder, setSortOrder] = useState(String(link.sort_order));

  const dirty =
    title !== link.title ||
    url !== link.url ||
    (icon || null) !== link.icon ||
    Number(sortOrder) !== link.sort_order;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${link.id}`, {
        location,
        parent_id: link.parent_id,
        title: title.trim(),
        url: url.trim(),
        icon: icon.trim() || null,
        badge_text: link.badge_text,
        badge_color: link.badge_color,
        open_in_new_tab: link.open_in_new_tab,
        sort_order: Number(sortOrder) || 0,
        is_visible: link.is_visible,
      });
    },
    onSuccess: () => {
      toast.success("Сохранено");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${link.id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: "Удалить ссылку?",
  });

  return (
    <div
      className={`grid grid-cols-12 gap-2 items-end rounded border p-2 ${dirty ? "border-primary/50" : ""}`}
    >
      <div className="col-span-12 md:col-span-4">
        <Label className="text-[10px]">Заголовок</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className={`col-span-12 ${showIcon ? "md:col-span-3" : "md:col-span-5"}`}>
        <Label className="text-[10px]">URL</Label>
        <div className="relative">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} />
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
      {showIcon && (
        <div className="col-span-8 md:col-span-2">
          <Label className="text-[10px]">Иконка</Label>
          <Input
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder={iconHint?.split(" / ")[0] ?? ""}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Label className="text-[10px]">№</Label>
        <Input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </div>
      <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "…" : "OK"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => askDelete(true, link.title)}
        >
          <Trash2 className="w-4 h-4 text-error" />
        </Button>
      </div>
      {deleteDialog}
    </div>
  );
}

function CreateInlineLink({
  location,
  showIcon,
  placeholderText,
  placeholderUrl,
  placeholderIcon,
  iconHint,
}: {
  location: string;
  showIcon: boolean;
  placeholderText: string;
  placeholderUrl: string;
  placeholderIcon?: string;
  iconHint?: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [icon, setIcon] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location,
        parent_id: null,
        title: title.trim(),
        url: url.trim(),
        icon: icon.trim() || null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: url.startsWith("http"),
        sort_order: 100,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success("Добавлено");
      setTitle("");
      setUrl("");
      setIcon("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setIcon(placeholderIcon ?? "");
          setOpen(true);
        }}
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        Добавить
      </Button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новая ссылка</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Текст</Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={placeholderText}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={placeholderUrl}
            />
          </div>
          {showIcon && (
            <div className="space-y-1.5">
              <Label className="text-xs">Иконка (slug)</Label>
              <Input
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder={placeholderIcon}
              />
              {iconHint && (
                <p className="text-[10px] text-muted-foreground">
                  Доступные slug'и: {iconHint}
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Columns tree (для footer_column) ──────────────────────────────────

function ColumnsTree({ links }: { links: NavLink[] }) {
  const columns = links
    .filter((l) => l.parent_id === null)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      {columns.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          Колонок пока нет. Добавь первую через кнопку ниже.
        </p>
      )}
      {columns.map((col) => (
        <ColumnCard key={col.id} column={col} allLinks={links} />
      ))}
      <CreateColumnDialog />
    </div>
  );
}

function ColumnCard({
  column,
  allLinks,
}: {
  column: NavLink;
  allLinks: NavLink[];
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(column.title);
  const [sortOrder, setSortOrder] = useState(String(column.sort_order));
  const [isVisible, setIsVisible] = useState(column.is_visible);

  const dirty =
    title !== column.title ||
    Number(sortOrder) !== column.sort_order ||
    isVisible !== column.is_visible;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${column.id}`, {
        location: "footer_column",
        parent_id: null,
        title: title.trim(),
        url: "",
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: Number(sortOrder) || 0,
        is_visible: isVisible,
      });
    },
    onSuccess: () => {
      toast.success("Колонка сохранена");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${column.id}`);
    },
    onSuccess: () => {
      toast.success("Колонка удалена");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: "Удалить колонку?",
    description:
      "Все вложенные ссылки и подзаголовки колонки тоже будут удалены.",
  });

  const children = allLinks
    .filter((l) => l.parent_id === column.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Card className="border-primary/30">
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-12 gap-2 items-end pb-3 border-b">
          <div className="col-span-12 md:col-span-7">
            <Label className="text-xs font-semibold">
              📁 Заголовок колонки
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="font-medium"
            />
          </div>
          <div className="col-span-4 md:col-span-2">
            <Label className="text-xs">№</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>
          <div className="col-span-8 md:col-span-3 flex justify-end gap-1">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer mr-2">
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => setIsVisible(e.target.checked)}
              />
              Видимо
            </label>
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              OK
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => askDelete(true, column.title)}
            >
              <Trash2 className="w-4 h-4 text-error" />
            </Button>
          </div>
        </div>

        {deleteDialog}

        {/* Дети колонки (ссылки и подзаголовки) */}
        <div className="pl-4 border-l-2 border-primary/20 space-y-2">
          {children.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Ссылок пока нет.
            </p>
          )}
          {children.map((child) => (
            <ChildRow key={child.id} link={child} allLinks={allLinks} />
          ))}
          <CreateChildDialog parentId={column.id} />
        </div>
      </CardContent>
    </Card>
  );
}

function ChildRow({
  link,
  allLinks,
}: {
  link: NavLink;
  allLinks: NavLink[];
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(link.title);
  const [url, setUrl] = useState(link.url);
  const [sortOrder, setSortOrder] = useState(String(link.sort_order));

  const isSubHeading = !link.url;
  const grandchildren = allLinks
    .filter((l) => l.parent_id === link.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  const dirty =
    title !== link.title ||
    url !== link.url ||
    Number(sortOrder) !== link.sort_order;

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/navigation/${link.id}`, {
        location: "footer_column",
        parent_id: link.parent_id,
        title: title.trim(),
        url: url.trim(),
        icon: link.icon,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: link.open_in_new_tab,
        sort_order: Number(sortOrder) || 0,
        is_visible: link.is_visible,
      });
    },
    onSuccess: () => {
      toast.success("Сохранено");
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const del = useMutation({
    mutationFn: async () => {
      await api.delete(`/navigation/${link.id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nav-links"] }),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => del.mutateAsync(),
    title: isSubHeading ? "Удалить подзаголовок?" : "Удалить ссылку?",
  });

  return (
    <div className="space-y-2">
      <div
        className={`grid grid-cols-12 gap-2 items-end rounded p-2 ${
          isSubHeading ? "bg-primary/5 border border-primary/20" : ""
        } ${dirty ? "ring-1 ring-primary/50" : ""}`}
      >
        <div className="col-span-12 md:col-span-4">
          <Label className="text-[10px]">
            {isSubHeading ? "📌 Подзаголовок" : "Текст"}
          </Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={isSubHeading ? "font-medium" : ""}
          />
        </div>
        <div className="col-span-9 md:col-span-5">
          <Label className="text-[10px]">URL (пусто = подзаголовок)</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Label className="text-[10px]">№</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
          <Button
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "…" : "OK"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => askDelete(true, link.title)}
          >
            <Trash2 className="w-3.5 h-3.5 text-error" />
          </Button>
        </div>
      </div>

      {deleteDialog}

      {/* Внуки */}
      {isSubHeading && (
        <div className="ml-6 pl-3 border-l border-border space-y-2">
          {grandchildren.map((gc) => (
            <ChildRow key={gc.id} link={gc} allLinks={allLinks} />
          ))}
          <CreateChildDialog parentId={link.id} />
        </div>
      )}
    </div>
  );
}

function CreateColumnDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location: "footer_column",
        parent_id: null,
        title: title.trim(),
        url: "",
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: 100,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success("Колонка создана");
      setTitle("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" />
        Добавить колонку
      </Button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новая колонка</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Заголовок</Label>
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: О компании"
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim()) create.mutate();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateChildDialog({ parentId }: { parentId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"link" | "subheading">("link");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      await api.post("/navigation", {
        location: "footer_column",
        parent_id: parentId,
        title: title.trim(),
        url: kind === "link" ? url.trim() : "",
        icon: null,
        badge_text: null,
        badge_color: null,
        open_in_new_tab: false,
        sort_order: 100,
        is_visible: true,
      });
    },
    onSuccess: () => {
      toast.success(kind === "link" ? "Ссылка добавлена" : "Подзаголовок добавлен");
      setTitle("");
      setUrl("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["nav-links"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setKind("link");
            setOpen(true);
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Ссылка
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setKind("subheading");
            setOpen(true);
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Подзаголовок
        </Button>
      </div>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {kind === "link" ? "Новая ссылка" : "Новый подзаголовок"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Текст</Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={kind === "link" ? "Реквизиты" : "Партнёрская программа"}
            />
          </div>
          {kind === "link" && (
            <div className="space-y-1.5">
              <Label className="text-xs">URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/about или https://…"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

